import Suspense from "components/shared/Suspense";
import Divider from "components/ui/Divider";
import ExpandingText from "components/ui/ExpandingText";
import Container from "hardcover-ui/components/Container";
import Text from "hardcover-ui/components/Text";
import TextLink from "hardcover-ui/components/TextLink";
import useIsLoggedIn from "hooks/user/useIsLoggedIn";
import BookPrompts from "./BookPrompts";
import BookSerieses from "./BookSerieses";
import TopBookReviews from "./reviews/TopBookReviews";
import TopBookLists from "./TopBookLists";
import SimilarBooks from "./SimilarBooks";
import BookButtons from "./BookButtons";
import LoggedInBookLinks from "./LoggedInBookLink";
import BookTags from "./BookTags";
import TopBookCharacters from "./TopBookCharacters";
import { usePage } from "@inertiajs/react";
import { BookPageProps } from "pages/clientverse/books/show.props";
import { lazy } from "react";
import LoadingBook from "hardcover-ui/components/LoadingBook";

const CurrentUserDetails = lazy(() => import("./CurrentUserDetails"));

export default function BookInfo() {
  const { page: book } = usePage<BookPageProps>().props;

  const isLoggedIn = useIsLoggedIn();
  return (
    <>
      <Container size="md" variant="layout">
        {book.description ? (
          <>
            {book.headline && (
              <Text
                size="sm"
                as="p"
                variant="secondary"
                className="uppercase mb-2 font-semibold tracking-wide"
              >
                {book.headline}
              </Text>
            )}
            <ExpandingText text={book.description} lines={6} />
          </>
        ) : (
          <Text variant="secondary" as="p" className="pt-4 italic">
            We don't have a description for this book yet. You can help out the
            author by{" "}
            <TextLink href={`/books/${book.id}/edit`}>
              adding a description
            </TextLink>
            .
          </Text>
        )}
      </Container>

      <Container size="md" variant="layout">
        <BookTags book={book} />
      </Container>


      <Container size="md">
        <Divider size="lg" />
      </Container>

      <Container
        variant="layout"
        size="md"
        className="flex justify-between items-center flex-wrap gap-2"
      >
        <BookButtons book={book} />
        <LoggedInBookLinks book={book} />
      </Container>


      <Container size="md">
        <Divider size="lg" />
      </Container>

      {isLoggedIn ? <Suspense fallback={<LoadingBook />}><CurrentUserDetails book={book} /></Suspense> : false}

      <BookSerieses book={book} bookSeries={book.bookSeries} />

      <TopBookReviews book={book} />

      <BookPrompts book={book} prompts={book.prompts} />

      <TopBookCharacters book={book} limit={9} />

      <TopBookLists book={book} topLists={book.lists} />

      <SimilarBooks similarBooks={book.similarBooks} />
    </>
  );
}
